import {
  JsonObject,
  JsonProperty,
  JsonConvert,
  ValueCheckingMode,
  JsonCustomConvert,
  JsonConverter,
  PropertyConvertingMode,
} from 'json2typescript';
import { Convert } from '../converter/utils';

export abstract class FeaturesTreeReader {
  abstract readTree(src: any, dst: any): void;
}

export abstract class FeaturesTreeReaderFactory {
  abstract create(): FeaturesTreeReader;
}

export abstract class FeaturesFactory {
  abstract createFromObject(features: any): any;
}

@JsonConverter
export class FeaturesConverter implements JsonCustomConvert<any> {
  serialize(data: any): any {
    return JSON.stringify(data);
  }

  deserialize(data: any): any {
    return JSON.parse(data);
  }
}

@JsonConverter
export class LocalDateTimeConverter implements JsonCustomConvert<Date> {
  serialize(data: Date): any {
    return {
      date: {
        year: data.getUTCFullYear(),
        month: data.getUTCMonth() + 1,
        day: data.getUTCDate(),
      },
      time: {
        hour: data.getUTCHours(),
        minute: data.getUTCMinutes(),
        second: data.getUTCSeconds(),
        nano: data.getUTCMilliseconds() * 1000000,
      },
    };
  }

  deserialize(data: any): Date {
    return new Date(
      Date.UTC(
        +data.date.year,
        data.date.month - 1,
        +data.date.day,
        +data.time.hour,
        +data.time.minute,
        +data.time.second,
        data.time.nano / 1000000,
      ),
    );
  }
}

@JsonObject('RequestEvent')
export class RequestEvent {
  @JsonProperty('id', String, PropertyConvertingMode.IGNORE_NULLABLE)
  id: string | undefined = undefined;
  @JsonProperty('correlationId', String, PropertyConvertingMode.IGNORE_NULLABLE)
  correlationId: string | undefined = undefined;
  @JsonProperty('type', String)
  type: string | undefined = undefined;
  @JsonProperty('created', LocalDateTimeConverter)
  created: Date | undefined = undefined;
  @JsonProperty('processed', LocalDateTimeConverter)
  processed: Date | undefined = undefined;
  @JsonProperty('user', String, PropertyConvertingMode.IGNORE_NULLABLE)
  user: string | undefined = undefined;
  @JsonProperty('stateChangeSystem', String, PropertyConvertingMode.IGNORE_NULLABLE)
  stateChangeSystem: string | undefined = undefined;
  @JsonProperty('comment', String)
  comment: string | undefined = undefined;
  @JsonProperty('location', String, PropertyConvertingMode.IGNORE_NULLABLE)
  location: string | undefined = undefined;
  @JsonProperty('tag', String, PropertyConvertingMode.IGNORE_NULLABLE)
  tag: string | undefined = undefined;
}

@JsonObject('RequestComment')
export class RequestComment {
  @JsonProperty('id', String)
  id: string | undefined = undefined;
  @JsonProperty('time', LocalDateTimeConverter)
  time: Date | undefined = undefined;
  @JsonProperty('user', String)
  user: string | undefined = undefined;
  @JsonProperty('text', String)
  text: string | undefined = undefined;
  @JsonProperty('alarmCenter', Boolean)
  alarmCenter: boolean | undefined = undefined;
  @JsonProperty('alarmObject', Boolean)
  alarmObject: boolean | undefined = undefined;
  @JsonProperty('deptCode', String)
  deptCode: string | undefined = undefined;
  @JsonProperty('eventId', String)
  eventId: string | undefined = undefined;
  @JsonProperty('canResetAlarm', Boolean)
  canResetAlarm: boolean | undefined = undefined;
  @JsonProperty('requestFK', String)
  requestFK: string | undefined = undefined;
}

@JsonObject('RequestBlob')
export class RequestBlob {
  @JsonProperty('tag', String)
  tag: string | undefined = undefined;
  @JsonProperty('data', String)
  data: string | undefined = undefined;
  @JsonProperty('dirty', Boolean)
  dirty: boolean | undefined = undefined;
}

@JsonObject('RequestRoot')
export class RequestRoot<T = any> {
  // Persistent
  @JsonProperty('id', String)
  id: string | undefined = undefined;
  @JsonProperty('uid', String)
  uid: string | undefined = undefined;
  @JsonProperty('status', String)
  status: string | undefined = undefined;
  @JsonProperty('eventHistory', [RequestEvent])
  eventHistory: RequestEvent[] | undefined = undefined;
  @JsonProperty('comments', [RequestComment], PropertyConvertingMode.IGNORE_NULLABLE)
  comments: RequestComment[] | undefined = undefined;
  @JsonProperty('features', FeaturesConverter)
  features: T | undefined = undefined;
  @JsonProperty('revision', String)
  revision: string | undefined = undefined;
  @JsonProperty('rawBlobs', [RequestBlob], PropertyConvertingMode.IGNORE_NULLABLE)
  rawBlobs: RequestBlob[] | undefined = undefined;
  @JsonProperty('imgBlobs', [RequestBlob], PropertyConvertingMode.IGNORE_NULLABLE)
  imgBlobs: RequestBlob[] | undefined = undefined;
  // Transient
  blobURLs = new Map<string, string>();

  readBlobURL(tag: string): string {
    const cached = this.blobURLs.get(tag);
    if (cached !== undefined) {
      return cached;
    }
    if (this.imgBlobs) {
      const img = this.imgBlobs.find((x) => x.tag === tag);
      if (img && img.data) {
        const result = Convert.wrapBase64ToDataUrl(img.data);
        this.blobURLs.set(tag, result);
        return result;
      }
    }
    if (this.rawBlobs) {
      const raw = this.rawBlobs.find((x) => x.tag === tag);
      if (raw && raw.data) {
        const result = Convert.wrapBase64ToDataUrl(raw.data);
        this.blobURLs.set(tag, result);
        return result;
      }
    }
    const result = '';
    this.blobURLs.set(tag, result);
    return result;
  }

  writeBlob(tag: string, _raw?: string, _img?: string) {
    let raw = _raw || '';
    raw = Convert.unwrapDataUrlToBase64(raw);
    let img = _img || '';
    img = Convert.unwrapDataUrlToBase64(img);
    this.blobURLs.delete(tag);
    if (this.imgBlobs) {
      const imgPos = this.imgBlobs.findIndex((x) => x.tag === tag);
      if (imgPos >= 0) {
        if (this.imgBlobs[imgPos].data !== img) {
          this.imgBlobs[imgPos].data = img;
          this.imgBlobs[imgPos].dirty = true;
        }
      } else {
        this.imgBlobs.push({ tag: tag, data: img, dirty: true });
      }
    }
    if (this.rawBlobs) {
      const rawPos = this.rawBlobs.findIndex((x) => x.tag === tag);
      if (rawPos >= 0) {
        if (this.rawBlobs[rawPos].data !== raw) {
          this.rawBlobs[rawPos].data = raw;
          this.rawBlobs[rawPos].dirty = true;
        }
      } else {
        this.rawBlobs.push({ tag: tag, data: raw, dirty: true });
      }
    }
  }

  writeObject(): any {
    const jsonConvert = new JsonConvert();
    jsonConvert.valueCheckingMode = ValueCheckingMode.DISALLOW_NULL;
    const result = jsonConvert.serialize(this);
    return result;
  }

  static init(uid: string, featuresRef: { new (): any }): RequestRoot {
    const result = new RequestRoot();
    result.id = '';
    result.uid = uid;
    result.status = '';
    result.eventHistory = [];
    result.comments = [];
    result.features = new featuresRef();
    result.revision = '';
    result.rawBlobs = [];
    result.imgBlobs = [];
    return result;
  }

  private static readFeaturesObj(r: RequestRoot, f: any, featureTreeReader: FeaturesTreeReader): void {
    featureTreeReader.readTree(r.features, f);
    r.features = f;
  }

  static readObject(object: any, featuresF: FeaturesFactory, featureTreeReaderF: FeaturesTreeReaderFactory): RequestRoot {
    const jsonConvert = new JsonConvert();
    jsonConvert.valueCheckingMode = ValueCheckingMode.DISALLOW_NULL;
    const result = jsonConvert.deserialize(object, RequestRoot) as RequestRoot;
    RequestRoot.readFeaturesObj(result, featuresF.createFromObject(result.features), featureTreeReaderF.create());
    return result;
  }

  static readObjects(objects: any, featuresF: FeaturesFactory, featureTreeReaderF: FeaturesTreeReaderFactory): RequestRoot[] {
    const jsonConvert = new JsonConvert();
    jsonConvert.valueCheckingMode = ValueCheckingMode.DISALLOW_NULL;
    const result = jsonConvert.deserialize(objects, RequestRoot) as RequestRoot[];
    result.forEach((x) => RequestRoot.readFeaturesObj(x, featuresF.createFromObject(x.features), featureTreeReaderF.create()));
    return result;
  }

  getStatusDate(): Date | null {
    if (!this.status || !this.eventHistory || this.eventHistory.length === 0) {
      return null;
    }
    const statusEvents = this.eventHistory
      .filter((e) => e.type === this.status && e.created)
      .sort((_1, _2) => {
        return _1.created!.getTime() - _2.created!.getTime();
      });
    if (statusEvents.length === 0) {
      return null;
    }
    return statusEvents[0].created!;
  }

  getLastModifiedDate(): Date | null {
    if (!this.status || !this.eventHistory || this.eventHistory.length === 0) {
      return null;
    }
    const event = this.eventHistory.reverse()[0];
    if (!event.processed) {
      return null;
    }
    return event.processed;
  }
}
